import React, { useEffect, useState } from 'react';
import { Stack, Box, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useResponsive from '../../hooks/useResponsive';
import Header from '../../layout/dashboard/header';
import ScheduleContent from '../../components/schedule/ScheduleContents';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { apiGetFacilitySchedules } from '../../api/schedules';
import { useNotificationService } from '../../components/notification';
import { ScheduleData } from '../../interface/schedule';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewIcon from '@mui/icons-material/GridView';
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from '../../interface/api_request';

const Schedule: React.FC = () => {
  const theme = useTheme();
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive('down', 'md', 'xl');
  const isSmall = useResponsive('down', 'sm', 'xl');
  const [schedule_list, setSchedule_list] = useState<ScheduleData[]>([]);

  const [view_mode, setView_mode] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [filterData, setFilterData] = useState('');

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData
  );

  useEffect(() => {
    mutation.mutate();
  }, [refresh, filterData, pagination_state.page]);

  const mutation = useMutation({
    mutationFn: () => apiGetFacilitySchedules(token),
    onSuccess: (data) => {
      if (data?.status === 'error') {
        if ('msg' in data) {
          showNotification(data?.msg ?? '', 'error');
          return;
        }
        return;
      }
      if ('data' in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setSchedule_list(data?.data?.data);
        } else {
          setSchedule_list([]);
        }
      }
    },
    onError: (error) => {
      showNotification('Fetch process failed', 'error');
    },
  });

  const handleViewMode = () => setView_mode(!view_mode);
  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            gap: '10px',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginRight: '1rem',
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={'row'} alignItems={'center'} sx={{ gap: '10px' }}>
            <Typography
              variant={'subtitle1'}
              sx={{
                fontWeight: 500,
                fontFamily: 'Lexend',
                color: '#2F66F4',
              }}
            >
              Schedule Management
            </Typography>
            {!isSmall && (
              <Button
                sx={{ height: 40, mx: 4 }}
                variant="contained"
                onClick={handleViewMode}
                startIcon={
                  !view_mode ? <FormatListBulletedIcon /> : <GridViewIcon />
                }
              >
                {!view_mode ? 'List' : 'Grid'}
              </Button>
            )}
          </Stack>
        </Box>
      </Header>

      <ScheduleContent
        viewMode={view_mode}
        setViewMode={handleViewMode}
        scheduleList={schedule_list}
        setRefresh={setRefresh}
        setFilterData={setFilterData}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
        loading={mutation.isPending}
      />
    </>
  );
};

export default Schedule;
